import React from 'react';
import { MapPin, Train, TreePine, Store, Compass } from 'lucide-react';

const LocationSection = () => {
  // Balcombe, West Sussex coordinates: 51.0531, -0.1342
  const mapSrc = "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d10000!2d-0.1342!3d51.0531!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4875f22d7e8b1b3b%3A0x7c6d6b6a6e1b8a0!2sBalcombe%2C%20Haywards%20Heath!5e0!3m2!1sen!2suk!4v1709830000000!5m2!1sen!2suk";

  return (
    <section className="py-10 md:py-16 px-4 md:px-6 bg-background">
      <div className="max-w-6xl mx-auto">
        <h2 className="text-xl md:text-2xl lg:text-3xl font-semibold text-foreground mb-2" style={{fontFamily: 'Cormorant Garamond, Georgia, serif'}}>
          Where you'll be
        </h2>
        <div className="flex items-center gap-2 text-muted-foreground mb-6 md:mb-8 text-sm md:text-base">
          <MapPin className="w-4 h-4" />
          <span>Balcombe, West Sussex, United Kingdom</span>
        </div>

        {/* Main content grid - Map + Neighbourhood info */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 mb-8">
          {/* Google Maps Embed */}
          <div className="rounded-xl md:rounded-2xl overflow-hidden shadow-lg border border-border/50">
            <iframe
              src={mapSrc}
              width="100%"
              height="350"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="The Hayloft Location - Balcombe, West Sussex"
              className="md:h-[400px]"
            />
          </div>

          {/* Neighbourhood Highlights */}
          <div className="bg-white rounded-xl md:rounded-2xl p-5 md:p-6 border border-border/50 shadow-sm">
            <h3 className="font-semibold text-foreground mb-3 md:mb-4 flex items-center gap-2 text-base md:text-lg" style={{fontFamily: 'Cormorant Garamond, Georgia, serif'}}>
              <Compass className="w-5 h-5 text-primary" />
              Neighbourhood highlights
            </h3>
            <p className="text-sm md:text-base text-muted-foreground leading-relaxed mb-4">
              We live in an Area of Outstanding Natural Beauty but only 15 minutes drive from Gatwick Airport. Win win! Quiet country lane - it doesn't get much more rural than here.
            </p>
            <p className="text-sm md:text-base text-muted-foreground leading-relaxed mb-4">
              One village pub in Staplefield and umpteen others a little further afield. M&S Simply Food at Pease Pottage. Village shops in Handcross, Balcombe and Cuckfield.
            </p>
            <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
              <span className="font-medium text-foreground">Cuckfield</span> is boutique heaven with gorgeous clothes, antique and lifestyle shops. <span className="font-medium text-foreground">Lindfield</span> is another beautiful village with a super selection of shops and restaurants. For bright lights, take the train to London or Brighton!
            </p>
          </div>
        </div>

        {/* Getting Around section */}
        <div className="bg-card rounded-xl md:rounded-2xl p-5 md:p-6 border border-border/50">
          <h3 className="font-semibold text-foreground mb-3 md:mb-4 flex items-center gap-2 text-base md:text-lg" style={{fontFamily: 'Cormorant Garamond, Georgia, serif'}}>
            <Train className="w-5 h-5 text-primary" />
            Getting around
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <Train className="w-4 h-4 text-primary" />
              </div>
              <div>
                <p className="font-medium text-foreground text-sm md:text-base">Public Transport</p>
                <p className="text-xs md:text-sm text-muted-foreground">
                  Balcombe train station is our closest, with trains to London or Brighton and anywhere in between. Local taxi firms also available.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <TreePine className="w-4 h-4 text-primary" />
              </div>
              <div>
                <p className="font-medium text-foreground text-sm md:text-base">Walking & Cycling</p>
                <p className="text-xs md:text-sm text-muted-foreground">
                  Great biking and walking country with endless foot and bridlepaths cutting through beautiful woods and farmland.
                </p>
              </div>
            </div>
          </div>
          <p className="text-xs md:text-sm text-muted-foreground mt-4 italic">
            Many have stayed here happily without a car, but ideally one would want a car when staying here.
          </p>
        </div>
      </div>
    </section>
  );
};

export default LocationSection;
